import type { NotificationSettings } from "@/api/notificationSettings";

/** 네이티브 앱 환경인지 확인 */
function isNativeApp(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return "NotificationBridge" in window;
}

/** DB 설정을 네이티브 브릿지 페이로드로 변환 */
function toNativePayload(settings: NotificationSettings) {
  return {
    enabled: settings.enabled,
    dailyReminder: settings.daily_reminder,
    dailyReminderTime: settings.daily_reminder_time,
    incompleteTest: settings.incomplete_test,
    wrongAnswerReview: settings.wrong_answer_review,
    wrongAnswerTiming: settings.wrong_answer_timing,
    streakReminder: settings.streak_reminder,
  };
}

/**
 * 네이티브 알림 스케줄 동기화 — 설정 변경 시 호출.
 * 권한 상태를 반환한다 (웹 환경이면 null).
 */
export async function syncNotificationsToNative(settings: NotificationSettings): Promise<string | null> {
  if (!isNativeApp()) {
    return null;
  }

  const bridge = (window as BridgeWindow).NotificationBridge!;

  if (!settings.enabled) {
    bridge.cancelAll();

    return "cancelled";
  }

  const status = await bridge.scheduleNotifications(toNativePayload(settings));

  return status;
}

/** 미완료 테스트 알림 예약 (1회성, 2시간 후) — 테스트 시작 시 호출 */
export function scheduleIncompleteTestReminder(): void {
  if (!isNativeApp()) {
    return;
  }

  (window as BridgeWindow).NotificationBridge!.scheduleIncompleteTestReminder();
}

/** 미완료 테스트 알림 취소 — 테스트 완료 시 호출 */
export function cancelIncompleteTestReminder(): void {
  if (!isNativeApp()) {
    return;
  }

  (window as BridgeWindow).NotificationBridge!.cancelIncompleteTestReminder();
}

/** 오답 복습 알림 예약 (1회성) — 시험 완료 시 오답이 있을 때만 호출 */
export function scheduleWrongAnswerReview(timing: "next_morning" | "after_hours"): void {
  if (!isNativeApp()) {
    return;
  }

  (window as BridgeWindow).NotificationBridge!.scheduleWrongAnswerReview(timing);
}

/** 스트릭 알림 예약 (1회성, 당일 오후 9시) — 앱 포그라운드 진입 시 호출 */
export function scheduleStreakReminder(): void {
  if (!isNativeApp()) {
    return;
  }

  (window as BridgeWindow).NotificationBridge!.scheduleStreakReminder();
}

/** 오늘 스트릭 알림 건너뛰기 — 테스트 완료 시 호출 (DAILY → 내일 1회성으로 교체) */
export function skipTodayStreakReminder(): void {
  if (!isNativeApp()) {
    return;
  }

  (window as BridgeWindow).NotificationBridge!.skipTodayStreakReminder();
}

/** 네이티브 알림 권한 상태 조회 */
export async function getNativePermissionStatus(): Promise<string | null> {
  if (!isNativeApp()) {
    return null;
  }

  const bridge = (window as BridgeWindow).NotificationBridge!;

  return bridge.getPermission();
}

// ── window 타입 확장 ──

interface NotificationBridgeAPI {
  scheduleNotifications(settings: ReturnType<typeof toNativePayload>): Promise<string>;
  cancelAll(): void;
  getPermission(): Promise<string>;
  scheduleIncompleteTestReminder(): void;
  cancelIncompleteTestReminder(): void;
  scheduleWrongAnswerReview(timing: "next_morning" | "after_hours"): void;
  scheduleStreakReminder(): void;
  skipTodayStreakReminder(): void;
}

type BridgeWindow = Window & { NotificationBridge?: NotificationBridgeAPI };
